import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Inbox,
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  ChevronRight,
  Bug,
  Gauge,
  Filter,
  Search,
  XCircle,
  Clock,
  CheckCircle2,
  Circle,
  Minus,
  ShieldAlert,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("academiaos_token");

// ── Status & type configs ──────────────────────────────────────────────────────

const STATUS_CFG = {
  SUBMITTED:        { label: "Submitted",        color: "text-sky-600 dark:text-sky-400",      bg: "bg-sky-50 dark:bg-sky-950/30",      border: "border-sky-200 dark:border-sky-800" },
  TRIAGED:          { label: "Triaged",           color: "text-violet-600 dark:text-violet-400", bg: "bg-violet-50 dark:bg-violet-950/30", border: "border-violet-200 dark:border-violet-800" },
  ACCEPTED:         { label: "Accepted",          color: "text-blue-600 dark:text-blue-400",    bg: "bg-blue-50 dark:bg-blue-950/30",    border: "border-blue-200 dark:border-blue-800" },
  IN_PROGRESS:      { label: "In Progress",       color: "text-amber-600 dark:text-amber-400",  bg: "bg-amber-50 dark:bg-amber-950/30",  border: "border-amber-200 dark:border-amber-800" },
  READY_FOR_RETEST: { label: "Ready for Retest",  color: "text-indigo-600 dark:text-indigo-400",bg: "bg-indigo-50 dark:bg-indigo-950/30",border: "border-indigo-200 dark:border-indigo-800" },
  RETEST_FAILED:    { label: "Retest Failed",     color: "text-red-600 dark:text-red-400",      bg: "bg-red-50 dark:bg-red-950/30",      border: "border-red-200 dark:border-red-800" },
  RETEST_PASSED:    { label: "Retest Passed",     color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200 dark:border-emerald-800" },
  CLOSED:           { label: "Closed",            color: "text-muted-foreground",               bg: "bg-muted/50",                       border: "border-border" },
  REJECTED:         { label: "Rejected",          color: "text-red-500 dark:text-red-400",      bg: "bg-red-50 dark:bg-red-950/30",      border: "border-red-200 dark:border-red-800" },
  DUPLICATE:        { label: "Duplicate",         color: "text-orange-500 dark:text-orange-400",bg: "bg-orange-50 dark:bg-orange-950/30",border: "border-orange-200 dark:border-orange-800" },
  DEFERRED:         { label: "Deferred",          color: "text-slate-500 dark:text-slate-400",  bg: "bg-slate-50 dark:bg-slate-950/30",  border: "border-slate-200 dark:border-slate-800" },
};

const SEVERITY_CFG = {
  CRITICAL:    { label: "Critical",    color: "text-red-600 dark:text-red-400",      dot: "bg-red-500" },
  HIGH:        { label: "High",        color: "text-orange-500 dark:text-orange-400", dot: "bg-orange-400" },
  MEDIUM:      { label: "Medium",      color: "text-amber-600 dark:text-amber-400",   dot: "bg-amber-400" },
  LOW:         { label: "Low",         color: "text-slate-500 dark:text-slate-400",   dot: "bg-slate-400" },
  INFORMATIONAL:{ label: "Info",       color: "text-blue-500 dark:text-blue-400",     dot: "bg-blue-400" },
};

const TYPE_CFG = {
  BUG:              { label: "Bug",            color: "text-red-600 dark:text-red-400" },
  USABILITY:        { label: "Usability",      color: "text-amber-600 dark:text-amber-400" },
  DATA_ISSUE:       { label: "Data Issue",     color: "text-orange-500 dark:text-orange-400" },
  CONFIGURATION:    { label: "Config",         color: "text-violet-500 dark:text-violet-400" },
  PERFORMANCE:      { label: "Performance",    color: "text-sky-500 dark:text-sky-400" },
  ENHANCEMENT:      { label: "Enhancement",    color: "text-emerald-500 dark:text-emerald-400" },
  QUESTION:         { label: "Question",       color: "text-blue-400" },
  SUGGESTION:       { label: "Suggestion",     color: "text-teal-500 dark:text-teal-400" },
};

const ALL_STATUSES = Object.keys(STATUS_CFG);
const ALL_SEVERITIES = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFORMATIONAL"];
const ALL_TYPES = Object.keys(TYPE_CFG);

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, color: "text-muted-foreground", bg: "bg-muted/50", border: "border-border" };
  return (
    <span className={`inline-flex items-center text-xs font-medium px-1.5 py-0.5 rounded border whitespace-nowrap ${cfg.bg} ${cfg.color} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

function SeverityDot({ severity }) {
  const cfg = SEVERITY_CFG[severity] || { label: severity, color: "text-muted-foreground", dot: "bg-muted-foreground" };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium whitespace-nowrap ${cfg.color}`}>
      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────

function StatsBar({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="uat-stats-bar">
      {[
        { label: "Total", value: stats.total, color: "text-foreground" },
        { label: "Open", value: stats.open_count, color: "text-amber-600 dark:text-amber-400" },
        { label: "Awaiting Triage", value: stats.submitted_count, color: "text-sky-600 dark:text-sky-400" },
        { label: "Critical / High", value: stats.critical_high_open, color: "text-red-600 dark:text-red-400" },
      ].map(({ label, value, color }) => (
        <div key={label} className="rounded-xl border border-border bg-card px-4 py-3">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className={`text-2xl font-bold mt-0.5 ${color}`}>{value ?? 0}</p>
        </div>
      ))}
    </div>
  );
}

// ── Filter Bar ────────────────────────────────────────────────────────────────

function FilterBar({ filters, onChange, onClear }) {
  return (
    <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="uat-filter-bar">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          placeholder="Search title…"
          value={filters.search}
          onChange={(e) => onChange("search", e.target.value)}
          className="h-8 pl-8 pr-3 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary/40 w-48"
          data-testid="uat-search-input"
        />
      </div>

      {[
        { key: "status", options: ALL_STATUSES, placeholder: "Status" },
        { key: "severity", options: ALL_SEVERITIES, placeholder: "Severity" },
        { key: "feedback_type", options: ALL_TYPES, placeholder: "Type" },
      ].map(({ key, options, placeholder }) => (
        <select
          key={key}
          value={filters[key]}
          onChange={(e) => onChange(key, e.target.value)}
          className="h-8 px-2.5 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
          data-testid={`uat-filter-${key}`}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o} value={o}>{o.replace(/_/g, " ")}</option>
          ))}
        </select>
      ))}

      {(filters.search || filters.status || filters.severity || filters.feedback_type) && (
        <button
          onClick={onClear}
          className="h-8 px-2.5 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 border border-border rounded-md"
          data-testid="uat-clear-filters"
        >
          <XCircle className="h-3.5 w-3.5" /> Clear
        </button>
      )}
    </div>
  );
}

// ── Feedback Row ─────────────────────────────────────────────────────────────

function FeedbackRow({ item, onNavigate }) {
  const shortId = item.id.slice(-6).toUpperCase();
  const typeCfg = TYPE_CFG[item.feedback_type] || { label: item.feedback_type, color: "text-muted-foreground" };

  return (
    <div
      data-testid={`feedback-row-${item.id}`}
      onClick={() => onNavigate(item.id)}
      className="group flex items-start gap-3 px-4 py-3 border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
    >
      {/* ID + type */}
      <div className="shrink-0 pt-0.5">
        <span className="text-[10px] font-mono text-muted-foreground/60">#{shortId}</span>
        <p className={`text-xs font-medium mt-0.5 ${typeCfg.color}`}>{typeCfg.label}</p>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <SeverityDot severity={item.severity} />
          {item.build_version && (
            <span className="text-xs text-muted-foreground/60 font-mono">v{item.build_version}</span>
          )}
          {item.environment && (
            <span className="text-xs text-muted-foreground/60">{item.environment}</span>
          )}
          {item.assigned_to && (
            <span className="text-xs text-muted-foreground/60 truncate max-w-20">→ {item.assigned_to.slice(-6)}</span>
          )}
        </div>
      </div>

      {/* Right side */}
      <div className="shrink-0 flex flex-col items-end gap-1.5">
        <StatusBadge status={item.status} />
        <span className="text-[10px] text-muted-foreground/60">
          {new Date(item.created_at).toLocaleDateString()}
        </span>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors shrink-0 mt-1.5" />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const EMPTY_FILTERS = { search: "", status: "", severity: "", feedback_type: "" };

export default function UATInbox() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const fetchAll = useCallback(async (f = filters, off = 0) => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: off });
      if (f.search) params.append("search", f.search);
      if (f.status) params.append("status", f.status);
      if (f.severity) params.append("severity", f.severity);
      if (f.feedback_type) params.append("feedback_type", f.feedback_type);

      const [listRes, statsRes] = await Promise.all([
        fetch(`${API}/api/uat/feedback?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/uat/feedback/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (!listRes.ok) throw new Error(`HTTP ${listRes.status}`);
      const listData = await listRes.json();
      setItems(listData.feedback || []);
      setTotal(listData.total || 0);

      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchAll(); }, []);

  const handleFilterChange = (key, value) => {
    const newF = { ...filters, [key]: value };
    setFilters(newF);
    setOffset(0);
    fetchAll(newF, 0);
  };

  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    setOffset(0);
    fetchAll(EMPTY_FILTERS, 0);
  };

  const handlePrev = () => {
    const newOff = Math.max(0, offset - LIMIT);
    setOffset(newOff);
    fetchAll(filters, newOff);
  };

  const handleNext = () => {
    const newOff = offset + LIMIT;
    setOffset(newOff);
    fetchAll(filters, newOff);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="uat-inbox-page">
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-5">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                data-testid="uat-inbox-back-btn"
                onClick={() => navigate("/setup")}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Configuration
              </button>
              <span className="text-muted-foreground/30">/</span>
              <div className="flex items-center gap-2">
                <Inbox className="h-5 w-5 text-foreground/70" />
                <div>
                  <h1 className="text-xl font-bold text-foreground" data-testid="uat-inbox-title">
                    UAT Inbox
                  </h1>
                  <p className="text-xs text-muted-foreground">Feedback triage and status tracking</p>
                </div>
              </div>
            </div>
            <button
              data-testid="uat-inbox-refresh-btn"
              onClick={() => fetchAll()}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1.5 rounded-md border border-border"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        {error && (
          <div className="flex items-center gap-3 p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 mb-4" data-testid="uat-inbox-error">
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        <StatsBar stats={stats} />

        <FilterBar filters={filters} onChange={handleFilterChange} onClear={handleClear} />

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="uat-feedback-list">
          {loading && (
            <div className="divide-y divide-border">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="px-4 py-3 flex gap-3 animate-pulse">
                  <div className="w-10 h-10 rounded bg-muted/50" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-muted/50 rounded w-3/4" />
                    <div className="h-2.5 bg-muted/50 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="uat-inbox-empty">
              <Inbox className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No feedback found</p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                {filters.status || filters.severity || filters.feedback_type || filters.search
                  ? "Try clearing the filters."
                  : "No UAT feedback has been submitted yet."}
              </p>
            </div>
          )}

          {!loading && items.map((item) => (
            <FeedbackRow
              key={item.id}
              item={item}
              onNavigate={(id) => navigate(`/setup/uat-inbox/${id}`)}
            />
          ))}
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div className="flex items-center justify-between mt-4" data-testid="uat-inbox-pagination">
            <p className="text-xs text-muted-foreground">
              {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
            </p>
            <div className="flex gap-2">
              <button onClick={handlePrev} disabled={offset === 0}
                className="h-8 px-3 text-xs rounded-md border border-border disabled:opacity-40">
                Previous
              </button>
              <button onClick={handleNext} disabled={offset + LIMIT >= total}
                className="h-8 px-3 text-xs rounded-md border border-border disabled:opacity-40">
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
